import React, { useState } from 'react'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { StateOfChargeSelector } from './state-of-charge-selector'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { updateTargetSoc } from '../../api'
import { TargetSocDto } from '../../dto'

export interface ChargeModalProps {
  onClose?: () => void
  place?: number
  vehicleId: number
  installationId: number
  targetSoc: number
  afterSaveCallback: (targetSoc: TargetSocDto['targetSoC']) => void
  chargeLimitMax?: number
}

/**
 * Dialog component for selecting the minimum charge level of vehicle using slider.
 * It handles cancelling and saving the data after selection.
 */
const ChargeModal: React.FC<ChargeModalProps> = props => {
  const { t } = useCustomTranslation()
  const { onClose, vehicleId, installationId, targetSoc, afterSaveCallback, chargeLimitMax } = props
  const [targetMinimumChargeLimit, setTargetMinimumChargeLimit] = useState<TargetSocDto['targetSoC']>(targetSoc)
  const [isDisabled, setIsDisabled] = useState(false)
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const authFetch = useAuthFetchWithResponse()

  const handleChargeLimitMin = (value: number): void => {
    setTargetMinimumChargeLimit(value)
  }

  const handleOnSave = async (): Promise<void> => {
    setIsDisabled(true)
    const hasValueChanged = targetMinimumChargeLimit !== targetSoc
    if (!hasValueChanged) {
      onClose?.()
      return
    }

    const { value, hasErrors } = await updateTargetSoc(vehicleId, installationId, targetMinimumChargeLimit, authFetch)

    if (hasErrors) {
      setIsDisabled(false)
      enqueueErrorStack(t('emobility.vehicle-integration.state-of-charge.snackbar-failure-message'))
      return
    }

    if (value !== null) {
      afterSaveCallback(value.targetSoC)
      enqueueSuccessStack(t('emobility.vehicle-integration.state-of-charge.snackbar-success-message'))
      onClose?.()
    }

    setIsDisabled(false)
  }

  const modalActions = {
    primary: {
      name: t('common.save'),
      onClick: handleOnSave,
      disabled: isDisabled,
    },
    secondary: { name: t('common.cancel') },
  }

  return (
    <Dialog title={t('emobility.vehicle-integration.state-of-charge.set-min-soc')} onClose={onClose} actions={modalActions} maxWidth="sm">
      <StateOfChargeSelector value={targetMinimumChargeLimit} onChange={handleChargeLimitMin} chargeLimitMax={chargeLimitMax} />
    </Dialog>
  )
}

export default ChargeModal
